<script setup>
/**
 * @important
 * The old Subjects page is now called Caap101SubjectsLegacy.vue.
 * This Subjects page displays a hardcoded allowlist of subtopics
 * (not every Firestore doc under reviewers/caap-101/subtopics)
 * and takes the user to the reading (i.e. practice) page.
 * 
 * Changes made by @john this Nov 13, 2025.
 */
import firebase from '@/firebase/config';
import { onMounted, onUnmounted, ref, computed } from 'vue';
import ROUTER from '@/router';
import LoadingCircle from '../components/LoadingCircle.vue';
import Announcement from '../components/Announcement.vue';
import { ChevronDoubleLeftIcon } from "@heroicons/vue/solid";
 
// Firestore (compat) instance
const db = firebase.firestore();

const isInitializing = ref(true); // if the page is being loaded, we set this to true. purpose: skeleton loader, etc.
const subtopics = ref([]);
// start of new subtopics flagging
const newSubtopics = ref([]);
const unsubscribeNewSubtopics = ref(null);
// end of new subtopics flagging
const sortedSubtopics = computed(() => {
    return subtopics.value.slice().sort((a, b) => {
        if (!a.name && !b.name) return 0;
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
});

const subjectId = ref('caap-101-the-license-exam');
const subjectName = ref('The CAAP License Exam');

const subtopicsToShow = ref([
    'obtain-your-personal-docs',
    'prepare-before-you-go',
    'the-licensure-journey-mnl',
    'tips--strategies-for-studying'
])

const tagsToShow = ref({
    'obtain-your-personal-docs': "prone",
    'prepare-before-you-go': "must-read",
    'the-licensure-journey-mnl': "prone",
    'tips--strategies-for-studying': "must-read"
})

/**
 * November 16, 2025:
 * Reads the single document at `settings/new-subtopics` which contains an `ids` array
 * of subtopic IDs flagged as "new". This is a global flag, not reviewer-specific.
 */
const getFlaggedNewSubtopics = async () => {
    try {
        const docSnap = await db.collection('settings').doc('new-subtopics').get();
        if (docSnap.exists) {
            const data = docSnap.data() || {};
            const ids = Array.isArray(data.ids) ? data.ids : [];
            return { isError: false, ids };
        } else {
            return { isError: true, ids: [] };
        }
    } catch (error) {
        console.error('Error fetching flagged new subtopics:', error);
        return { isError: true, ids: [] };
    }
}

/**
 * November 16, 2025:
 * Subscribes to the `settings/new-subtopics` document and calls the `onChange` function
 * with the new subtopic IDs.
 */
const subscribeFlaggedNewSubtopics = (onChange) => {
    try {
        const ref = db.collection('settings').doc('new-subtopics');
        const unsubscribe = ref.onSnapshot((docSnap) => {
            const ids = docSnap.exists && Array.isArray(docSnap.data().ids) ? docSnap.data().ids : [];
            onChange({ isError: false, ids });
        }, (error) => {
            onChange({ isError: true, ids: [], error });
        });
        return unsubscribe;
    } catch (error) {
        console.error('Error subscribing to flagged new subtopics:', error);
        return null;
    }
}

/**
 * November 16, 2025:
 * Gets the new subtopics from the database.
 */
const getNewSubtopics = async () => {
    const res = await getFlaggedNewSubtopics();
    newSubtopics.value = res.ids;
}

const fetchSubtopicById = (subtopicId) => {
    return new Promise(async (resolve, reject) => {

        // console.log("Fetching subject data.")
        firebase.firestore().collection("reviewers").doc("caap-101").collection("subtopics").doc(subtopicId).get()
        .then((doc) => {
          if (doc.exists) {
            let data = doc.data()
            data.id = doc.id
            resolve(data);
          } else {
            reject("Subtopic not found");
          }
        })
        .catch((error) => {
          console.error("Error fetching subtopic data => ", error);
          reject(error);
        });

       

    })
}

/**
 * When the component or view is mounted, we fetch the caap 101 subjects
 * and create order on how they will be displayed in the UI.
 */
onMounted(async () => {
    for (let index = 0; index < subtopicsToShow.value.length; index++) {
        const subtopicId = subtopicsToShow.value[index];
        subtopics.value.push(await fetchSubtopicById(subtopicId));
    }
    isInitializing.value = false;

    // start of new subtopics flagging
    getNewSubtopics(); 
    // live updates for flagged new subtopics
    unsubscribeNewSubtopics.value = subscribeFlaggedNewSubtopics(({ ids }) => {
        newSubtopics.value = ids;
    });
    // end of new subtopics flagging
})

onUnmounted(() => {
    console.log('onUnmounted fired');
    // unsubscribe from new subtopics flagging
    if (unsubscribeNewSubtopics.value) {
      unsubscribeNewSubtopics.value();
      unsubscribeNewSubtopics.value = null;
    }
})


const handlePracticeClick = (subtopic) => {
    ROUTER.push({ path: `/caap-101/practice`, query: { subtopicId: subtopic.id, subtopicName: subtopic.name, subjectId: subjectId.value, subjectName: subjectName.value } })
}

</script>

<template>
    <div class="max-w-7xl mx-auto py-2">
        <!-- <Announcement /> -->
        <div class="space-y-6 mb-20 px-4">
            <nav class="flex justify-between items-center" aria-label="Breadcrumb">
                <div class="flex items-center">
                    <router-link to="/reviewers"
                        class="text-sm font-medium text-warm-gray-500 hover:text-warm-gray-600 flex items-center"
                        aria-current="page">
                        <ChevronDoubleLeftIcon class="flex-shrink-0 h-6 w-6" aria-hidden="true" />
                    </router-link>
                </div>
                <div class="text-sm font-medium text-warm-gray-500">
                    CAAP 101
                </div>
                <div class="w-6 h-6"></div>
            </nav>
            <div class="caap-101">
                <ul v-if="!isInitializing" class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <li v-for="subtopic in sortedSubtopics" :key="subtopic.id">
                        <div class="card-base">
                            <div class="p-6">
                                <h1 class="card-heading">{{ subtopic.name }}</h1>
                                <p class="card-description">{{ subtopic.description }}</p>
                                <p class="card-tags">
                                    <span v-if="tagsToShow[subtopic.id] === 'must-read'" class="badge-base badge-base--yellow">
                                        🔥 Must Read
                                    </span>
                                    <span v-if="tagsToShow[subtopic.id] === 'prone'" class="badge-base badge-base--indigo">
                                        🌀 Prone To Changes
                                    </span>
                                    <span v-if="newSubtopics.includes(subtopic.id)" class=" inline-flex items-center rounded-md bg-green-400 bg-opacity-10 px-1 py-0.5 text-xs font-medium text-green-400 border border-green-500 border-opacity-20">
                                        New
                                    </span>
                                </p>
                            </div>
                            <div class="card-footer">
                                <div class="text-sm flex justify-between">
                                    <button class="card-button" type="button" @click="handlePracticeClick(subtopic)">
                                        Read Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
                <div v-else>
                    <LoadingCircle />
                </div>

            </div>
        </div>
    </div>


</template>

<style scoped>
.caap-101 {
    @apply text-warm-gray-700 dark:text-warm-gray-400
}

/*.caap-101 .card-list {
    @apply space-y-8
}*/

.caap-101 .card-base {
    @apply text-base rounded-2xl border border-true-gray-400 dark:border-true-gray-700 dark:bg-true-gray-800 dark:bg-opacity-50 bg-white border-opacity-50 dark:border-opacity-50
}

.caap-101 .card-heading {
    @apply text-xl font-medium dark:text-warm-gray-400 text-warm-gray-800
}

.caap-101 .card-description {
    @apply text-sm text-warm-gray-500 mt-4
}

/* .card-footer {
    @apply dark:bg-true-gray-700 dark:bg-opacity-25 bg-gray-50 flex-1 min-w-0 px-6 py-3 rounded-b-2xl
} */

/* .card-footer {
    @apply px-6 pb-5
} */

.card-footer {
    @apply dark:bg-true-gray-700 dark:bg-opacity-25 bg-gray-50 flex-1 min-w-0 px-6 py-3 rounded-b-2xl
}

/* .caap-101 .card-button {
    @apply border border-true-gray-300 dark:border-true-gray-700 px-4 dark:bg-true-gray-800 py-2 rounded-md hover:opacity-75 transition-all w-full
} */

.card-tags {
    @apply text-sm text-warm-gray-500 mt-4 space-x-2
}

.caap-101 .card-button {
    @apply font-medium dark:text-yellow-400 dark:hover:text-yellow-300 text-yellow-600 hover:text-yellow-700
}

.badge-base {
    @apply inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset
}

.badge-base--indigo {
    @apply bg-indigo-400 bg-opacity-10 text-indigo-400 ring-indigo-400 ring-opacity-30
}

.badge-base--yellow {
    @apply bg-yellow-400 bg-opacity-10 text-yellow-500 ring-yellow-400 ring-opacity-30 dark:text-yellow-400
}
</style>